import * as io from "io-ts";
import type { RawFailureValues } from "../../../../../api/useIndexedFailureValues";
import { ActiveAnalysisColumns } from "./analysis";
import type { Setup } from "../../../../../api/useSetups";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import { withMessage } from "io-ts-types";
import t from "../../../../../i18n";
import type { Quantity } from "../../../../../api/useQuantities";

/**
 * @description
 * Quantity constant columns.
 *
 * @see {/projects/:projectId(uuid)/quantities}
 */
export const QuantityConstantColumns = io.keyof({
  description: null,
  equipmentIds: null,
  equipmentTypeId: null,
  formula: null,
  maxActivation: null,
  maxMetrology: null,
  maxStdMetrology: null,
  minActivation: null,
  minMetrology: null,
  name: null,
  sensor: null,
  systemId: null,
  type: null,
  maxStdActivation: null,
  /**
   * @note
   * The following columns are considered required by default.
   * They will be appended to the list of columns if not provided.
   */
  id: null,
  unit: null,
});

/**
 * @description
 * Quantity analysis value columns.
 * Depends on QuantityConstantColumns.id.
 *
 * @see {/projects/:projectId(uuid)/quantities}
 * @see {/projects/:projectId(uuid)/active-analyses}
 */
export const QuantityAnalysisValueColumns = io.keyof({
  measured: null,
  simulated: null,
  std: null,
});

/**
 * @description
 * Quantity setup input columns.
 * Depends on QuantityConstantColumns.id
 * Maps to Setup.diagnosisInputs.
 *
 * @see {/projects/:projectId(uuid)/quantities}
 * @see {/projects/:projectId(uuid)/active-analyses}
 * @see {/projects/:projectId(uuid)/setups}
 */
export const QuantityAnalysisSetupInputColumns = io.keyof({
  enabled: null,
  sigma: null,
});

/**
 * @description
 * Quantity setup output columns.
 * Depends on QuantityConstantColumns.id
 * Maps to Setup.simulationOutputs.
 *
 * @see {/projects/:projectId(uuid)/quantities}
 * @see {/projects/:projectId(uuid)/active-analyses}
 * @see {/projects/:projectId(uuid)/setups}
 */
export const QuantityAnalysisSetupOutputColumns = io.keyof({
  offsetExpected: null,
});

/**
 * @todo SetupComputed Description
 * @description
 */
export const QuantityAnalysisSetupComputedColumns = io.keyof({
  expected: null,
  gap: null,
  normalizedGap: null,
  validity: null,
  externalImpacts: null,
});

export const QuantitySetupColumns = io.union([
  QuantityAnalysisSetupInputColumns,
  QuantityAnalysisSetupOutputColumns,
  QuantityAnalysisSetupComputedColumns,
]);

export const QuantityColumnList = [
  QuantityConstantColumns,
  ActiveAnalysisColumns,
  QuantityAnalysisValueColumns,
  QuantityAnalysisSetupInputColumns,
  QuantityAnalysisSetupOutputColumns,
  QuantityAnalysisSetupComputedColumns,
].flatMap(t => Object.keys(t.keys));

export const QuantityColumns = withMessage(
  io.union([
    QuantityConstantColumns,
    ActiveAnalysisColumns,
    QuantityAnalysisValueColumns,
    QuantitySetupColumns,
  ]),
  input => t("schema.column", `${input}`, QuantityColumnList.join(", ")),
);

export type QuantityColumnsT = io.TypeOf<typeof QuantityColumns>;

/**
 * @description
 * Quantity available resource columns.
 * We use typescript Pick type against our data model
 * to keep our schema consistant against the API.
 */
export type QuantityConstantColumnsT = keyof Pick<
  Quantity,
  io.TypeOf<typeof QuantityConstantColumns>
>;

export type ActiveAnalysisColumnsT = keyof Pick<
  ActiveAnalysis,
  io.TypeOf<typeof ActiveAnalysisColumns>
>;

export type QuantityAnalysisValueColumnsT = keyof Pick<
  ActiveAnalysis["quantityValues"][string],
  io.TypeOf<typeof QuantityAnalysisValueColumns>
>;

export type QuantityAnalysisSetupInputColumnsT = keyof Pick<
  Setup["diagnosisInputs"][number],
  io.TypeOf<typeof QuantityAnalysisSetupInputColumns>
>;

export type QuantityAnalysisSetupOutputColumnsT = keyof Pick<
  Setup["simulationOutputs"][number],
  io.TypeOf<typeof QuantityAnalysisSetupOutputColumns>
>;

export type QuantityAnalysisSetupComputedColumnsT = io.TypeOf<
  typeof QuantityAnalysisSetupComputedColumns
>;

/**
 * @description
 * Fault constant columns.
 * Maps to setup.failures
 *
 * @see {/projects/:projectId(uuid)/setups}
 */
export const FaultConstantColumns = io.keyof({
  enabled: null,
  name: null,
  description: null,
  min: null,
  max: null,
  amplitude: null,
  systemId: null,
  equipmentTypeId: null,
  degradationTypeId: null,
  failureCategoryId: null,
  impacts: null,
  filters: null,
  displayName: null,
  /**
   * @note
   * The following columns are considered required by default.
   * They will be appended to the list of columns if not provided.
   */
  id: null,
  unit: null,
});

/**
 * @description
 * Fault analysis value columns.
 * Depends on FaultConstantColumns.id.
 *
 * @see {/projects/:projectId(uuid)/failure-values}
 */
export const FaultValueColumns = io.keyof({
  /** @note collision w/ analysis */
  //   faultStatus: null,
  real: null,
  confidence: null,
});

export const FaultComputedValueColumns = io.keyof({
  quantityImpacts: null,
  globalImpact: null,
  faultStatus: null,
});

export type FaultConstantColumnsT = keyof Pick<
  Setup["failures"][number],
  io.TypeOf<typeof FaultConstantColumns>
>;

export type FaultValueColumnsT = keyof Pick<
  RawFailureValues[number] & { faultStatus: string },
  io.TypeOf<typeof FaultValueColumns>
>;

export type FaultComputedValueColumnsT = io.TypeOf<typeof FaultComputedValueColumns>;

export const FaultEventColumns = io.keyof({
  eventId: null,
  eventHasMultiFault: null,
  eventType: null,
  eventName: null,
  eventPriority: null,
  eventStartsAt: null,
  eventEndsAt: null,
  eventFaultName: null,
});

export const FaultColumnsList = [
  FaultConstantColumns,
  ActiveAnalysisColumns,
  FaultValueColumns,
  FaultComputedValueColumns,
  FaultEventColumns,
].flatMap(t => Object.keys(t.keys));

export const FaultColumns = io.union([
  FaultConstantColumns,
  ActiveAnalysisColumns,
  FaultValueColumns,
  FaultComputedValueColumns,
  FaultEventColumns,
]);

export type FaultColumnsT = io.TypeOf<typeof FaultColumns>;

export const Columns = withMessage(io.union([FaultColumns, QuantityColumns]), input =>
  t("schema.column", `${input}`, [...QuantityColumnList, ...FaultColumnsList].join(", ")),
);
